import { useQuery } from "@tanstack/react-query";
import OCGatewayClient from "../client/OCGatewayClient";
import { DailyActivityAggregate, MonthlyActivityAggregate } from "../models/ActivityStats";
import { gatewayUserScopedQueryOptions } from "./gatewayQueryUtils";

export interface CatActivityStatsResponse {
    from: string;
    to: string;
    /** deviceId -> IANA time zone the day buckets are on (null when unset). */
    timeZones: Record<string, string | null>;
    days: DailyActivityAggregate[];
}

export interface CatMonthlyActivityStatsResponse {
    from: string;
    to: string;
    timeZones: Record<string, string | null>;
    months: MonthlyActivityAggregate[];
}

export interface ActivityStatsQueryOptions {
    /**
     * Admin only: query the fleet-wide adminGetActivityStats command by
     * rfidCode alone, so stats resolve for chips on devices the viewer does
     * not own (support lookups, shared-event links). The gateway rejects it
     * for non-admin users, so gate on userLevel before enabling.
     */
    fleetWide?: boolean;
}

function statsRequest(rfidCode: string, deviceIds: string[], from: string, to: string, groupBy: 'day' | 'month', fleetWide: boolean) {
    return OCGatewayClient.request(
        fleetWide ? 'adminGetActivityStats' : 'getActivityStats',
        {
            ...(fleetWide ? {} : { deviceIds }),
            rfidCode,
            from,
            to,
            groupBy,
            // The profile modal opens for hidden visitors too; hiding only
            // affects the activity overview, not the cat's own stats.
            includeHidden: true
        }
    );
}

/**
 * Daily activity rollups for one chip, summed across the given devices.
 * Days are DEVICE-local calendar days; days with no activity have no row.
 * The rollups are maintained asynchronously by the stats worker, so a short
 * stale time is enough — there is no realtime update stream for them.
 */
export function useCatActivityStatsQuery(rfidCode: string, deviceIds: string[], from: string, to: string, options?: ActivityStatsQueryOptions) {
    const fleetWide = options?.fleetWide === true;
    return useQuery({
        queryKey: ['getActivityStats', rfidCode, from, to, fleetWide ? 'fleet' : deviceIds.join(',')],
        queryFn: async (): Promise<CatActivityStatsResponse> => {
            const response: any = await statsRequest(rfidCode, deviceIds, from, to, 'day', fleetWide);

            if (response?.code && response.code !== 200) {
                throw new Error(response.message || `getActivityStats failed (${response.code})`);
            }

            return response as CatActivityStatsResponse;
        },
        enabled: !!rfidCode && (fleetWide || deviceIds.length > 0),
        ...gatewayUserScopedQueryOptions,
        staleTime: 1000 * 60 * 5
    });
}

/** Per-month rollups for one chip; months without activity have no row. */
export function useCatMonthlyActivityStatsQuery(rfidCode: string, deviceIds: string[], from: string, to: string, options?: ActivityStatsQueryOptions) {
    const fleetWide = options?.fleetWide === true;
    return useQuery({
        queryKey: ['getActivityStats', 'month', rfidCode, from, to, fleetWide ? 'fleet' : deviceIds.join(',')],
        queryFn: async (): Promise<CatMonthlyActivityStatsResponse> => {
            const response: any = await statsRequest(rfidCode, deviceIds, from, to, 'month', fleetWide);

            if (response?.code && response.code !== 200) {
                throw new Error(response.message || `getActivityStats failed (${response.code})`);
            }

            return response as CatMonthlyActivityStatsResponse;
        },
        enabled: !!rfidCode && (fleetWide || deviceIds.length > 0),
        ...gatewayUserScopedQueryOptions,
        staleTime: 1000 * 60 * 5
    });
}
